/**
 * React Query hooks over the Listings API (./listingsApi.ts) — the Listings
 * screens' data seam. One query key family (`listingsKeys`); every write
 * invalidates what it changes:
 *
 *   agreement-scoped writes (units, listings, tasks, steps, documents, the
 *   agreement itself) → that board, its activity, its research matches (M30:
 *   a property just listed is no longer suggested), every agreement list
 *   (counts) and my-tasks;
 *   create agreement → the agreement lists;
 *   template writes → the templates;
 *   the compliance settings are the settings registry group listings.compliance
 *   (CONFIG CF-11): its save refreshes every agreement read and my-tasks (the
 *   expiry flags are worked out with them) — api/extensions/settings_registry;
 *   Team workload (M22a) is refreshed by every agreement-scoped write (a
 *   reassignment is useUpdateListingTask) and by a listings.workload save;
 *   so are the New broker requests (M74: a request sent, or assigned).
 *
 * Optimistic only where it is trivially safe: a step's check box and a task's
 * priority flag patch the cached board at once and roll back on an error.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ListingDeactivationReason } from '@aiga/app-config/listings';
import { listingsApi, type AgreementListParams } from './listingsApi';
import type {
  AddTaskInput,
  AddUnitInput,
  AgreementBoard,
  AgreementFileKind,
  AgreementInput,
  AgreementLink,
  AgreementListRow,
  CompleteTaskInput,
  ListingInput,
  ListingTask,
  MyTasks,
  TaskInput,
  TemplateInput,
  TemplateTrack,
} from './types';
import type { TaskPriority } from '../priority';

export const listingsKeys = {
  /** Root of every Listings API query ('listings' alone is the generated CRUD hooks' key). */
  all: ['listings-api'] as const,
  agreements: () => [...listingsKeys.all, 'agreements'] as const,
  agreementList: (params: AgreementListParams) => [...listingsKeys.agreements(), 'list', params] as const,
  board: (agreementId: string) => [...listingsKeys.agreements(), 'board', agreementId] as const,
  /** Under the board, so every write that invalidates the board signs the thumbnails again. */
  boardPreviews: (agreementId: string, fileIds: readonly string[]) => [...listingsKeys.board(agreementId), 'previews', fileIds.join(',')] as const,
  activityRoot: (agreementId: string) => [...listingsKeys.agreements(), 'activity', agreementId] as const,
  activity: (agreementId: string, listingId: string | null) => [...listingsKeys.activityRoot(agreementId), listingId] as const,
  activityLatest: (agreementId: string, eventType: string) => [...listingsKeys.activityRoot(agreementId), 'latest', eventType] as const,
  /** Under the feed's root, so every agreement write refreshes it (invalidateAgreement). */
  activityOfType: (agreementId: string, eventType: string) => [...listingsKeys.activityRoot(agreementId), 'type', eventType] as const,
  /** One task's history (M206), under the feed's root as well. */
  taskActivity: (agreementId: string, taskId: string) => [...listingsKeys.activityRoot(agreementId), 'task', taskId] as const,
  researchMatches: (agreementId: string) => [...listingsKeys.agreements(), 'research-matches', agreementId] as const,
  myTasks: () => [...listingsKeys.all, 'my-tasks'] as const,
  myTasksFor: (params: Record<string, unknown>) => [...listingsKeys.myTasks(), params] as const,
  teamWorkload: () => [...listingsKeys.all, 'team-workload'] as const,
  teamWorkloadFor: (params: Record<string, unknown>) => [...listingsKeys.teamWorkload(), params] as const,
  teamWorkloadByAssignerFor: (params: Record<string, unknown>) => [...listingsKeys.teamWorkload(), 'by-assigner', params] as const,
  brokerRequests: () => [...listingsKeys.all, 'broker-requests'] as const,
  templates: () => [...listingsKeys.all, 'templates'] as const,
  templatesFor: (includeArchived: boolean) => [...listingsKeys.templates(), includeArchived] as const,
  unitSearch: (q: string, limit: number) => [...listingsKeys.all, 'unit-search', q, limit] as const,
  /** M226: a property's marketing documents (./propertyDocuments.ts); its thumbnails' URLs sit under it. */
  propertyDocuments: (propertyId: string) => [...listingsKeys.all, 'property-documents', propertyId] as const,
};

/**
 * After a write on one agreement: its board and feed, the lists' counts, the dashboards (My work, Team workload,
 * New broker requests), every property's marketing documents (M226: which properties a file reaches is the server's),
 * and every listing's marketing details (M226: a collateral upload ticks Flyer / OM / Sale summary on the server;
 * platformKeys.marketing's root, spelled here because platformHooks.ts imports this file).
 */
export function invalidateAgreement(qc: QueryClient, agreementId: string): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: listingsKeys.board(agreementId) }),
    qc.invalidateQueries({ queryKey: listingsKeys.activityRoot(agreementId) }),
    qc.invalidateQueries({ queryKey: listingsKeys.researchMatches(agreementId) }),
    qc.invalidateQueries({ queryKey: [...listingsKeys.agreements(), 'list'] }),
    qc.invalidateQueries({ queryKey: listingsKeys.myTasks() }),
    qc.invalidateQueries({ queryKey: listingsKeys.teamWorkload() }),
    qc.invalidateQueries({ queryKey: listingsKeys.brokerRequests() }),
    qc.invalidateQueries({ queryKey: [...listingsKeys.all, 'property-documents'] }),
    qc.invalidateQueries({ queryKey: [...listingsKeys.all, 'marketing'] }),
  ]);
}

/** The board with one task changed (undefined when the board or task is not cached). */
export function withTask(board: AgreementBoard | undefined, taskId: string, change: (t: ListingTask) => ListingTask): AgreementBoard | undefined {
  if (!board) return board;
  let found = false;
  const listings = board.listings.map((l) => ({
    ...l,
    tasks: l.tasks.map((t) => {
      if (t.id !== taskId) return t;
      found = true;
      return change(t);
    }),
  }));
  return found ? { ...board, listings } : board;
}

// ── Reads ────────────────────────────────────────────────────────────────────

export function useListingAgreements(params: AgreementListParams, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: listingsKeys.agreementList(params), queryFn: () => listingsApi.listAgreements(params), enabled });
}

/** Every agreement matching the filters, read in API-sized pages (GET /agreements caps a page at 200). */
async function listAllAgreements(params: Omit<AgreementListParams, 'page' | 'limit'>): Promise<AgreementListRow[]> {
  const limit = 200;
  const first = await listingsApi.listAgreements({ ...params, page: 1, limit });
  const rows = [...first.rows];
  for (let page = 2; rows.length < first.total && page <= Math.ceil(first.total / limit); page++) {
    rows.push(...(await listingsApi.listAgreements({ ...params, page, limit })).rows);
  }
  return rows;
}

/** Every Active agreement the caller may see (the Expiring view groups them), read in API-sized pages. */
export function useActiveAgreements() {
  return useQuery({
    queryKey: [...listingsKeys.agreements(), 'list', 'active-all'] as const,
    queryFn: () => listAllAgreements({ status: 'ACTIVE' }),
  });
}

/** M29b: every agreement matching the list filters, not one page (the projects map), read in API-sized pages. */
export function useAllListingAgreements(params: Omit<AgreementListParams, 'page' | 'limit'>, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...listingsKeys.agreements(), 'list', 'all', params] as const,
    queryFn: () => listAllAgreements(params),
    enabled,
  });
}

export function useListingBoard(agreementId: string | null) {
  return useQuery({
    queryKey: listingsKeys.board(agreementId ?? ''),
    queryFn: () => listingsApi.getBoard(agreementId as string),
    enabled: !!agreementId,
    // A hidden or unknown agreement answers 404: show it, do not retry it.
    retry: false,
  });
}

/** Under the signed URLs' hour (3600 s), so a thumbnail never asks with an expired link. */
const PREVIEW_URLS_STALE_MS = 50 * 60 * 1000;

/** The Documents thumbnails' signed URLs (file id → URL), one call for the agreement; off when no shown row can preview. */
export function useAgreementFilePreviewUrls(agreementId: string, fileIds: readonly string[]) {
  return useQuery({
    queryKey: listingsKeys.boardPreviews(agreementId, fileIds),
    queryFn: () => listingsApi.getFilePreviewUrls(agreementId),
    enabled: fileIds.length > 0,
    staleTime: PREVIEW_URLS_STALE_MS,
    select: (d) => d.urls,
  });
}

/** Activity rows per page (the API's default is 50). */
export const ACTIVITY_PAGE_SIZE = 25;

/** The agreement's feed, newest first; `fetchNextPage` follows the API's cursor ("Load more"). */
export function useListingActivity(agreementId: string | null, listingId: string | null = null) {
  return useInfiniteQuery({
    queryKey: listingsKeys.activity(agreementId ?? '', listingId),
    queryFn: ({ pageParam }) =>
      listingsApi.getActivity(agreementId as string, { listing_id: listingId, cursor: pageParam, limit: ACTIVITY_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: !!agreementId,
  });
}

/** The agreement's newest feed row of one type, or null (Agreement details' Latest update: who wrote the status note, and when). */
export function useLatestListingActivity(agreementId: string | null, eventType: string) {
  return useQuery({
    queryKey: listingsKeys.activityLatest(agreementId ?? '', eventType),
    queryFn: async () => (await listingsApi.getActivity(agreementId as string, { event_types: eventType, limit: 1 })).items[0] ?? null,
    enabled: !!agreementId,
  });
}

/** Rows per page of one feed row type (Agreement details' Earlier updates). */
export const ACTIVITY_TYPE_PAGE_SIZE = 10;

/**
 * The agreement's feed rows of one type, newest first, in pages (`fetchNextPage` follows the cursor): Agreement
 * details' Earlier updates read every AGREEMENT_STATUS_NOTE_CHANGED row this way (meeting 2026-10-22, item 24).
 */
export function useListingActivityOfType(agreementId: string | null, eventType: string) {
  return useInfiniteQuery({
    queryKey: listingsKeys.activityOfType(agreementId ?? '', eventType),
    queryFn: ({ pageParam }) =>
      listingsApi.getActivity(agreementId as string, { event_types: eventType, cursor: pageParam, limit: ACTIVITY_TYPE_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled: !!agreementId,
  });
}

/** Rows per page of a task's history. */
export const TASK_ACTIVITY_PAGE_SIZE = 20;

/**
 * One task's feed rows, newest first, in pages (Task focus → History, M206). For a listings_work viewer the API
 * includes the step ticks, which the agreement feed leaves out; pass enabled: false until the list is opened.
 */
export function useTaskActivity(agreementId: string, taskId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: listingsKeys.taskActivity(agreementId, taskId),
    queryFn: ({ pageParam }) => listingsApi.getActivity(agreementId, { task_id: taskId, cursor: pageParam, limit: TASK_ACTIVITY_PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
}

export function useListingMyTasks(params: { scope?: 'me' | 'all'; assignee?: string | null; from?: string; to?: string }) {
  return useQuery({ queryKey: listingsKeys.myTasksFor(params), queryFn: () => listingsApi.getMyTasks(params) });
}

/** Team workload (M22a): an admin's read — pass enabled: false for anyone else (the API answers 403). */
export function useListingTeamWorkload(params: { from?: string; to?: string }, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: listingsKeys.teamWorkloadFor(params), queryFn: () => listingsApi.getTeamWorkload(params), enabled });
}

/** Team workload, Assigned by (M22c): under the same key family, so the same writes refresh it. */
export function useListingTeamWorkloadByAssigner(params: { from?: string; to?: string }, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: listingsKeys.teamWorkloadByAssignerFor(params),
    queryFn: () => listingsApi.getTeamWorkloadByAssigner(params),
    enabled,
  });
}

/** M74: the New broker requests — listings_work only: pass enabled: false for anyone else (the API answers 403). */
export function useListingBrokerRequests({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: listingsKeys.brokerRequests(), queryFn: () => listingsApi.getBrokerRequests(), enabled });
}

export function useListingTemplates(includeArchived = false) {
  return useQuery({ queryKey: listingsKeys.templatesFor(includeArchived), queryFn: () => listingsApi.listTemplates(includeArchived) });
}

/** The unit search; `q` is the debounced term (below 2 characters nothing is asked). */
export function useListingUnitSearch(q: string, limit = 20) {
  return useQuery({
    queryKey: listingsKeys.unitSearch(q, limit),
    queryFn: () => listingsApi.searchUnits(q, limit),
    enabled: q.trim().length >= 2,
  });
}

// ── Agreement writes ─────────────────────────────────────────────────────────

export function useCreateListingAgreement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AgreementInput) => listingsApi.createAgreement(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: listingsKeys.agreements() }),
  });
}

/** A write on one agreement; `run` gets the variables, the agreement is refreshed after. */
function useAgreementWrite<V, R>(agreementId: string, run: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: run, onSettled: () => invalidateAgreement(qc, agreementId) });
}

export const useUpdateListingAgreement = (agreementId: string) =>
  useAgreementWrite(agreementId, (body: AgreementInput) => listingsApi.updateAgreement(agreementId, body));

/** M198: one property's links on the agreement, saved whole. */
export const useSetPropertyLinks = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ propertyId, links }: { propertyId: string; links: AgreementLink[] }) =>
    listingsApi.setPropertyLinks(agreementId, propertyId, links),
  );

export const useAddListedUnit = (agreementId: string) =>
  useAgreementWrite(agreementId, (body: AddUnitInput) => listingsApi.addUnit(agreementId, body));

export const useUpdateListing = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ listingId, body }: { listingId: string; body: ListingInput }) => listingsApi.updateListing(listingId, body));

// ── Compliance (L3-1) ────────────────────────────────────────────────────────

export const useCloseListingAgreement = (agreementId: string) =>
  useAgreementWrite(agreementId, (note: string | null) => listingsApi.closeAgreement(agreementId, note));

/** `expirationDate`: a new expiry, required when the old one has passed. */
export const useReopenListingAgreement = (agreementId: string) =>
  useAgreementWrite(agreementId, (expirationDate: string | null) => listingsApi.reopenAgreement(agreementId, expirationDate));

/** M18: a new expiry (after the old one) and an optional note; the old term stays in the feed. */
export const useRenewListingAgreement = (agreementId: string) =>
  useAgreementWrite(agreementId, (body: { expiration_date: string; note?: string | null }) => listingsApi.renewAgreement(agreementId, body));

/** M202: On hold on / off (the board and the feed refresh). */
export const useSetAgreementHold = (agreementId: string) =>
  useAgreementWrite(agreementId, (onHold: boolean) => listingsApi.setAgreementHold(agreementId, onHold));

/** M202: Mark audited (today); clears the audit-overdue chip. */
export const useAuditAgreement = (agreementId: string) => useAgreementWrite(agreementId, () => listingsApi.auditAgreement(agreementId));

/** "Not this listing": the deal is no longer offered on that listing. */
export const useDismissListingDeal = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ listingId, transactionId }: { listingId: string; transactionId: string }) =>
    listingsApi.dismissDeal(listingId, transactionId),
  );

/** M196: ACTIVE to UNDER_CONTRACT; the answer carries `agreement_warning` when the agreement has expired. */
export const useConvertToDeal = (agreementId: string) => useAgreementWrite(agreementId, (listingId: string) => listingsApi.convertToDeal(listingId));

/** M222 "Give it its own workflow" on an associated row (listings_work; Active or Under contract, an open agreement). */
export const usePromoteListing = (agreementId: string) => useAgreementWrite(agreementId, (listingId: string) => listingsApi.promoteListing(listingId));

/** M196 "Deactivate <track> workflow" on a lane (listings_work; Active or Under contract, an open agreement), with its reason. */
export const useWithdrawTrack = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ listingId, reason, note }: { listingId: string; reason: ListingDeactivationReason; note: string | null }) =>
    listingsApi.withdrawTrack(listingId, { reason, note }),
  );

// ── The request to Research (M30) ────────────────────────────────────────────

/** Properties that look like the answer to an open request to Research (listings_work: pass enabled=false otherwise). */
export function useResearchMatches(agreementId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: listingsKeys.researchMatches(agreementId), queryFn: () => listingsApi.getResearchMatches(agreementId), enabled });
}

/** "Use this property": the request is answered with it (the submission acknowledged if Research has not yet). */
export const useAnswerResearchMatch = (agreementId: string) =>
  useAgreementWrite(agreementId, (body: { broker_submission_id: string; property_id: string }) => listingsApi.answerResearchMatch(agreementId, body));

/** "Not this one": that property is not suggested for that request again. */
export const useDismissResearchMatch = (agreementId: string) =>
  useAgreementWrite(agreementId, (body: { broker_submission_id: string; property_id: string }) => listingsApi.dismissResearchMatch(agreementId, body));

// ── Task writes ──────────────────────────────────────────────────────────────

export const useAddListingTask = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ listingId, body }: { listingId: string; body: AddTaskInput }) => listingsApi.addTask(listingId, body));

export const useUpdateListingTask = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ taskId, body }: { taskId: string; body: TaskInput }) => listingsApi.updateTask(taskId, body));

/**
 * M74: assign a New broker request to a person. `if_unassigned`: a request another assistant
 * took meanwhile answers 409 ALREADY_ASSIGNED rather than being taken over.
 */
export function useAssignBrokerRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, assigneeId }: { taskId: string; agreementId: string; assigneeId: string }) =>
      listingsApi.updateTask(taskId, { assignee_user_profile_id: assigneeId, if_unassigned: true }),
    onSettled: (_r, _e, { agreementId }) => invalidateAgreement(qc, agreementId),
  });
}

export const useCompleteListingTask = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ taskId, body }: { taskId: string; body: CompleteTaskInput }) => listingsApi.completeTask(taskId, body));

export const useReopenListingTask = (agreementId: string) =>
  useAgreementWrite(agreementId, (taskId: string) => listingsApi.reopenTask(taskId));

/** M16 2/3: Confirm what the app saw (→ Human confirmed). */
export const useConfirmListingTask = (agreementId: string) => useAgreementWrite(agreementId, (taskId: string) => listingsApi.confirmTask(taskId));

/** M16 2/3: Not yet (→ Open), with the required note. */
export const useNotYetListingTask = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ taskId, note }: { taskId: string; note: string }) => listingsApi.notYetTask(taskId, note));

/** M186 + D1: take an open task (not a takedown) off its lane (the board and the feed refresh). */
export const useRemoveListingTask = (agreementId: string) =>
  useAgreementWrite(agreementId, (taskId: string) => listingsApi.removeTask(taskId));

/** A board-cached optimistic write: patch the task now, roll back on an error, refresh after. */
function useOptimisticTaskWrite<V extends { taskId: string }>(
  agreementId: string,
  run: (vars: V) => Promise<unknown>,
  change: (t: ListingTask, vars: V) => ListingTask,
) {
  const qc = useQueryClient();
  const key = listingsKeys.board(agreementId);
  return useMutation({
    mutationFn: run,
    onMutate: async (vars: V) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<AgreementBoard>(key);
      qc.setQueryData<AgreementBoard | undefined>(key, (b) => withTask(b, vars.taskId, (t) => change(t, vars)));
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () => invalidateAgreement(qc, agreementId),
  });
}

export const useSetListingTaskStep = (agreementId: string) =>
  useOptimisticTaskWrite(
    agreementId,
    ({ stepId, done }: { taskId: string; stepId: string; done: boolean }) => listingsApi.setStepDone(stepId, done),
    // M206: a tick shows its time at once (the server keeps the first tick's time and person); the name arrives with the refetch.
    (t, { stepId, done }) => ({
      ...t,
      steps: t.steps.map((s) =>
        s.id !== stepId || s.done === done
          ? s
          : done
            ? { ...s, done, done_at: new Date().toISOString(), done_by_user_profile_id: null, done_by_name: null }
            : { ...s, done, done_at: null, done_by_user_profile_id: null, done_by_name: null },
      ),
    }),
  );

/** A task's priority level (M184). */
export const useSetListingTaskPriority = (agreementId: string) =>
  useOptimisticTaskWrite(
    agreementId,
    ({ taskId, priority }: { taskId: string; priority: TaskPriority }) => listingsApi.updateTask(taskId, { priority }),
    (t, { priority }) => ({ ...t, priority }),
  );

/**
 * My work's priority picker (M184 levels): the task can be on any agreement, so it patches
 * every cached my-tasks answer at once (rolled back on an error) and then
 * refreshes that task's agreement (its board, feed and my-tasks).
 */
export function useSetMyTaskPriority() {
  const qc = useQueryClient();
  const key = listingsKeys.myTasks();
  return useMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; agreementId: string; priority: TaskPriority }) => listingsApi.updateTask(taskId, { priority }),
    onMutate: async ({ taskId, priority }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueriesData<MyTasks>({ queryKey: key });
      qc.setQueriesData<MyTasks | undefined>({ queryKey: key }, (d) =>
        d ? { ...d, tasks: d.tasks.map((t) => (t.id === taskId ? { ...t, priority } : t)) } : d,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      for (const [k, d] of ctx?.previous ?? []) qc.setQueryData(k, d);
    },
    onSettled: (_r, _e, { agreementId }) => invalidateAgreement(qc, agreementId),
  });
}

// ── Agreement documents ──────────────────────────────────────────────────────

/** `propertyId` (M198): the property on the agreement the document is about; omitted = the whole agreement. */
export const useUploadAgreementFile = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ file, kind, propertyId }: { file: File; kind: AgreementFileKind; propertyId?: string | null }) =>
    listingsApi.uploadFile(agreementId, file, kind, propertyId),
  );
/** M198: a document's kind and / or property (`property_id: null` = the whole agreement). */
export const useUpdateAgreementFile = (agreementId: string) =>
  useAgreementWrite(agreementId, ({ fileId, ...body }: { fileId: string; kind?: AgreementFileKind; property_id?: string | null }) =>
    listingsApi.updateFile(agreementId, fileId, body),
  );

export const useDeleteAgreementFile = (agreementId: string) =>
  useAgreementWrite(agreementId, (fileId: string) => listingsApi.deleteFile(agreementId, fileId));
export const useArchiveAgreementFile = (agreementId: string) =>
  useAgreementWrite(agreementId, (fileId: string) => listingsApi.archiveFile(agreementId, fileId));
export const useRestoreAgreementFile = (agreementId: string) =>
  useAgreementWrite(agreementId, (fileId: string) => listingsApi.restoreFile(agreementId, fileId));

/** Opening a document asks for a fresh 1-hour signed URL each time (not cached). */
export function useOpenAgreementFile(agreementId: string) {
  return useMutation({ mutationFn: (fileId: string) => listingsApi.getFileUrl(agreementId, fileId) });
}

// ── Templates (feature:listings_templates) ───────────────────────────────────

function useTemplateWrite<V, R>(run: (vars: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: run, onSettled: () => qc.invalidateQueries({ queryKey: listingsKeys.templates() }) });
}

export const useCreateListingTemplate = () => useTemplateWrite((body: TemplateInput) => listingsApi.createTemplate(body));
export const useUpdateListingTemplate = () =>
  useTemplateWrite(({ templateId, body }: { templateId: string; body: TemplateInput }) => listingsApi.updateTemplate(templateId, body));
export const useArchiveListingTemplate = () =>
  useTemplateWrite(({ templateId, archived }: { templateId: string; archived: boolean }) => listingsApi.setTemplateArchived(templateId, archived));
export const useReorderListingTemplates = () =>
  useTemplateWrite(({ track, templateIds }: { track: TemplateTrack; templateIds: string[] }) => listingsApi.reorderTemplates(track, templateIds));
